import type { ComponentPropsWithoutRef, ElementType, ReactNode, Ref } from 'react'

import { cn } from '@gto/ui/cn'

import { paperVariants } from './paper.styles'

type PaperElevation =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'
  | '24'

type PaperVariant = 'elevation' | 'outlined'

type BasePaperProps = {
  /** Elevation level 0-24, maps to `bg-paper-elevation{N}`. Ignored when `variant="outlined"`. */
  elevation?: PaperElevation
  /** `"elevation"` for filled surface, `"outlined"` for elevation-0 + border. */
  variant?: PaperVariant
  /** When true, no border radius. Default: rounded-sm (6px). */
  square?: boolean
  /** Additional CSS class names */
  className?: string
  /** Paper content */
  children?: ReactNode
  /** React 19 ref prop */
  ref?: Ref<HTMLElement>
  /** Test attribute for E2E testing */
  'data-tst'?: string
}

type PaperProps<T extends ElementType = 'div'> = BasePaperProps & {
  /** Polymorphic element type. Default: `'div'`. */
  as?: T
  /** @deprecated Use `as` prop instead */
  component?: T
} & Omit<ComponentPropsWithoutRef<T>, keyof BasePaperProps | 'as' | 'component'>

/**
 * Paper — foundational surface primitive.
 *
 * Conveys elevation through background color only (no drop shadows). If a floating
 * surface needs a shadow, apply it via `className` (e.g. `className="shadow-8"`).
 *
 * @example
 * <Paper elevation="4">Content</Paper>
 *
 * @example
 * <Paper variant="outlined">With border</Paper>
 *
 * @example
 * <Paper as="section" elevation="2" className="p-2">Section content</Paper>
 */
const Paper = <T extends ElementType = 'div'>(props: PaperProps<T>) => {
  const {
    as,
    // oxlint-disable-next-line typescript/no-deprecated
    component,
    elevation = '1',
    variant = 'elevation',
    square = false,
    className,
    children,
    ref,
    ...rest
  } = props

  const Component: ElementType = as ?? component ?? 'div'

  return (
    <Component
      ref={ref as Ref<never>}
      className={cn(paperVariants({ variant, elevation, square }), className)}
      {...rest}
    >
      {children}
    </Component>
  )
}

export { Paper }
export type { PaperElevation, PaperProps, PaperVariant }
